import {
  Flex,
  SimpleGrid,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ActionButton } from '../../../../components/ActionButton'
import { BackLink } from '../../../../components/BackLink'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { EmptyCell } from '../../../../components/EmptyCell'
import { EmptyState } from '../../../../components/EmptyState'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { LoadingBlock, TableSkeletonRows } from '../../../../components/Loading'
import { PageTitle } from '../../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { SectionCard } from '../../../../components/SectionCard'
import ShortText from '../../../../components/ShortText'
import {
  formatAverage,
  formatNpsScore,
  formatPercentage,
  formatScore,
  MetricCard,
  npsColor,
  NpsBands,
  responsesLabel
} from '../../../../components/SurveyMetrics'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { Tooltip } from '../../../../components/Tooltip'
import { ApiErrorShape, formatDateTime, useAppToast, useDownload, useHasRole } from '../../../../helpers'
import { EventSurveyService } from '../../../../services/Event/EventSurveyService'
import { IEventSurveyEvent, IEventSurveyReport, IEventSurveyResponseRow } from '../../../../services/Event/surveyTypes'
import { ISurveyQuestion } from '../../../../services/Survey/types'
import EventSurveyModal from './EventSurveyModal'
import EventSurveySendModal from './EventSurveySendModal'

/** O que o relatório pode ser: carregado, ainda sem pesquisa (404) ou falha de verdade. */
type TReportState = 'loading' | 'ready' | 'not_configured' | 'error'

const invitedLabel = (count: number) => `${count} ${count === 1 ? 'convidado' : 'convidados'}`

/**
 * Resultados da pesquisa NPS de um evento (ABRA-370): NPS, taxa de resposta, média de cada
 * pergunta de nota e a listagem das respostas, com nome e e-mail de quem respondeu — o link é
 * individual, então a resposta é identificada.
 */
export default function EventSurveyResultsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canManage = hasRole('manage_event_survey')
  const canViewEvent = hasRole('view_events')
  const service = useMemo(() => new EventSurveyService(), [])
  const { isOpen: isConfigOpen, onOpen: onConfigOpen, onClose: onConfigClose } = useDisclosure()
  const { isOpen: isSendOpen, onOpen: onSendOpen, onClose: onSendClose } = useDisclosure()

  const [event, setEvent] = useState<IEventSurveyEvent | null>(null)
  const [loadingEvent, setLoadingEvent] = useState(true)

  const [report, setReport] = useState<IEventSurveyReport | null>(null)
  const [reportState, setReportState] = useState<TReportState>('loading')

  const [responses, setResponses] = useState<IEventSurveyResponseRow[]>([])
  const [loadingResponses, setLoadingResponses] = useState(true)
  const [responsesError, setResponsesError] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(1)

  const [responseToDelete, setResponseToDelete] = useState<IEventSurveyResponseRow | null>(null)
  const { isDownloading, download } = useDownload()

  // Descarta respostas atrasadas quando busca e página mudam em sequência rápida.
  const responsesRequestIdRef = useRef(0)

  useEffect(() => {
    setLoadingEvent(true)
    // Não usa o detalhe do evento (getBySlug): ele exige view_events, e esta página é aberta
    // também por quem só tem a permissão de resultados.
    service
      .getEvent(slug!)
      .then(setEvent)
      .catch((error) => {
        toast.error('Não foi possível carregar o evento', error)
        navigate(canViewEvent ? '/events' : '/')
      })
      .finally(() => setLoadingEvent(false))
  }, [slug])

  const loadReport = (eventId: number) => {
    setReportState('loading')
    service
      .report(eventId)
      .then((result) => {
        setReport(result)
        setReportState('ready')
      })
      .catch((error) => {
        setReport(null)

        // 404 é "o evento ainda não tem pesquisa", que é um estado da tela e não uma falha.
        if ((error as ApiErrorShape).response?.status === 404) {
          setReportState('not_configured')
          return
        }

        setReportState('error')
        toast.error('Erro ao carregar o relatório da pesquisa', error)
      })
  }

  const loadResponses = (eventId: number, pageToLoad: number) => {
    const requestId = ++responsesRequestIdRef.current
    setLoadingResponses(true)
    setResponsesError(false)
    service
      .responses(eventId, { page: pageToLoad, page_size: pageSize, search })
      .then((result) => {
        if (requestId !== responsesRequestIdRef.current) return
        setResponses(result.data)
        setTotalPages(Math.ceil(result.total_filtered / (result.per_page || 1)) || 1)
      })
      .catch((error) => {
        if (requestId !== responsesRequestIdRef.current) return
        setResponsesError(true)
        setResponses([])
        setTotalPages(1)
        toast.error('Erro ao carregar as respostas', error)
      })
      .finally(() => {
        if (requestId === responsesRequestIdRef.current) setLoadingResponses(false)
      })
  }

  useEffect(() => {
    if (event) loadReport(event.id)
  }, [event])

  // A listagem só existe depois que o relatório confirmou que há pesquisa.
  useEffect(() => {
    if (event && reportState === 'ready') loadResponses(event.id, page)
  }, [event, reportState, page, pageSize, search])

  const questionColumns = useMemo(
    () => [...(report?.questions ?? [])].sort((a, b) => a.position - b.position),
    [report]
  )

  const handleDelete = () => {
    if (!event || !responseToDelete) return Promise.resolve()

    return service
      .deleteResponse(event.id, responseToDelete.id)
      .then(() => {
        toast.success('Resposta excluída')
        setResponseToDelete(null)
        loadReport(event.id)

        // Apagar o único item da página deixaria a tela parada numa página que não existe mais.
        if (responses.length === 1 && page > 1) setPage(page - 1)
      })
      .catch((error) => toast.error('Erro ao excluir a resposta', error))
  }

  const handleConfigClose = () => {
    onConfigClose()
    // A configuração pode ter criado a pesquisa ou mudado as perguntas: os números e as colunas
    // da listagem dependem disso.
    if (event) loadReport(event.id)
  }

  const renderAnswer = (response: IEventSurveyResponseRow, question: ISurveyQuestion) => {
    const answer = response.answers.find((item) => item.survey_question_id === question.id)

    if (question.type === 'scale') {
      return <EmptyCell value={answer?.value_numeric == null ? null : formatScore(answer.value_numeric)} />
    }
    if (!answer?.value_text) return <EmptyCell />

    return <ShortText text={answer.value_text} beforeChars={60} afterChars={0} />
  }

  if (loadingEvent) return <LoadingBlock size="page" entity="evento" />
  if (!event) return null

  const npsScaleQuestion = report?.scale_questions.find((question) => question.is_nps) ?? null
  const scaleCards = report?.scale_questions.filter((question) => !question.is_nps) ?? []
  const invitations = report?.invitations
  const columnsCount = 2 + questionColumns.length + (canManage ? 1 : 0)

  const configureButton = canManage && (
    <ActionButton
      action="settings"
      label={reportState === 'not_configured' ? 'Configurar pesquisa' : 'Editar pesquisa'}
      showLabel
      mr={0}
      onClick={onConfigOpen}
    />
  )
  const sendButton = canManage && reportState === 'ready' && (
    <ActionButton action="send" label="Enviar pesquisa" showLabel mr={0} onClick={onSendOpen} />
  )

  return (
    <>
      {/* Quem só tem a permissão de resultados não abre o detalhe do evento (view_events). */}
      {canViewEvent && (
        <BackLink to={`/events/${event.slug}`} mb={4}>
          Voltar para o evento
        </BackLink>
      )}

      <PageTitle
        title={`Pesquisa NPS · ${event.name}`}
        uppercase={false}
        description="Respostas da pesquisa de satisfação enviada por e-mail aos participantes"
        // Sem pesquisa o botão fica no próprio estado vazio, que é para onde o olho vai.
        actions={
          reportState === 'not_configured' ? undefined : (
            <Flex gap={2}>
              {sendButton}
              {configureButton}
            </Flex>
          )
        }
        mb="5"
      />

      {reportState === 'not_configured' ? (
        <EmptyState
          variant="panel"
          icon="lni lni-comments"
          title="Pesquisa NPS não configurada"
          description={
            canManage
              ? 'Configure a pesquisa para enviar links individuais aos participantes deste evento.'
              : 'Este evento ainda não tem pesquisa NPS.'
          }
          action={configureButton || undefined}
        />
      ) : reportState === 'error' ? (
        <EmptyState
          variant="panel"
          tone="error"
          title="Não foi possível carregar a pesquisa"
          action={
            <ActionButton
              action="refresh"
              label="Tentar novamente"
              showLabel
              mr={0}
              onClick={() => loadReport(event.id)}
            />
          }
        />
      ) : (
        <>
          {/* MÉTRICAS */}
          {reportState === 'loading' && !report ? (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="4" mb="6">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} height="118px" borderRadius="14px" />
              ))}
            </SimpleGrid>
          ) : (
            report && (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="4" mb="6">
                {report.nps && (
                  <MetricCard
                    label={`NPS · ${report.nps.short_label}`}
                    value={formatNpsScore(report.nps.score)}
                    valueColor={npsColor(report.nps.score)}
                    hint={
                      npsScaleQuestion
                        ? `Média ${formatAverage(npsScaleQuestion.average)} · ${responsesLabel(
                            report.nps.answers_count
                          )}`
                        : responsesLabel(report.nps.answers_count)
                    }
                    tooltip={npsScaleQuestion?.statement}
                  >
                    <NpsBands
                      promoters={report.nps.promoters}
                      passives={report.nps.passives}
                      detractors={report.nps.detractors}
                    />
                  </MetricCard>
                )}
                {invitations && (
                  <MetricCard
                    label="Taxa de resposta"
                    value={invitations.response_rate === null ? '—' : `${formatPercentage(invitations.response_rate)}%`}
                    hint={`${invitations.responded} de ${invitedLabel(invitations.invited)}`}
                    tooltip="Participantes que receberam o link por e-mail e responderam a pesquisa"
                    delay={0.04}
                  />
                )}
                {scaleCards.map((question, index) => (
                  <MetricCard
                    key={question.question_id}
                    label={`Média · ${question.short_label}`}
                    value={formatAverage(question.average)}
                    hint={`De 0 a 10 · ${responsesLabel(question.answers_count)}`}
                    tooltip={question.statement}
                    delay={0.08 + index * 0.04}
                  />
                ))}
              </SimpleGrid>
            )
          )}

          {/* RESPOSTAS */}
          <FilterBar onClear={() => setSearch('')} isDirty={!!search}>
            <FilterSearch
              value={search}
              onChange={(value) => {
                setSearch(value)
                setPage(1)
              }}
              placeholder="nome / e-mail"
            />
          </FilterBar>

          <SectionCard
            variant="flush"
            title="Respostas"
            actions={
              <ActionButton
                action="download"
                label="Exportar XLSX"
                showLabel
                mr={0}
                isLoading={isDownloading}
                loadingText="Exportando..."
                isDisabled={reportState !== 'ready'}
                onClick={() =>
                  download(() => service.exportXlsx(event.id, event.slug, search), 'Erro ao exportar as respostas')
                }
              />
            }
          >
            <TableContainer>
              <Table>
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Data</Th>
                    <Th>Participante</Th>
                    {questionColumns.map((question) => (
                      <Th key={question.id}>
                        <Tooltip label={question.statement}>
                          <Text as="span" tabIndex={0} cursor="help" borderBottom="1px dashed" borderColor="gray.400">
                            {question.short_label}
                          </Text>
                        </Tooltip>
                      </Th>
                    ))}
                    {canManage && <Th>Ações</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {loadingResponses || reportState === 'loading' ? (
                    <TableSkeletonRows cols={columnsCount} />
                  ) : responsesError ? (
                    <TableErrorRow
                      colSpan={columnsCount}
                      entity="respostas"
                      feminine
                      onRetry={() => loadResponses(event.id, page)}
                    />
                  ) : responses.length === 0 ? (
                    <TableEmptyRow colSpan={columnsCount} entity="resposta" feminine />
                  ) : (
                    responses.map((response) => (
                      <Tr key={response.id} _hover={{ bg: 'gray.50' }}>
                        <Td whiteSpace="nowrap">{formatDateTime(response.created_at)}</Td>
                        <Td>
                          <EmptyCell display="block" value={response.respondent_name} />
                          {response.respondent_email && <Text variant="caption">{response.respondent_email}</Text>}
                        </Td>
                        {questionColumns.map((question) => (
                          <Td key={question.id}>{renderAnswer(response, question)}</Td>
                        ))}
                        {canManage && (
                          <Td>
                            <ActionButton action="delete" mr={0} onClick={() => setResponseToDelete(response)} />
                          </Td>
                        )}
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </SectionCard>

          <Paginator
            page={page}
            totalPages={totalPages}
            changePage={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        </>
      )}

      {canManage && (
        <>
          <EventSurveyModal
            isOpen={isConfigOpen}
            onClose={handleConfigClose}
            eventId={event.id}
            eventName={event.name}
            eventSlug={event.slug}
            eventImage={event.thumb_wide || event.thumb}
          />
          <EventSurveySendModal
            isOpen={isSendOpen}
            onClose={onSendClose}
            eventId={event.id}
            eventName={event.name}
            onDispatched={() => loadReport(event.id)}
          />
          <ConfirmDialog
            isOpen={!!responseToDelete}
            onClose={() => setResponseToDelete(null)}
            onConfirm={handleDelete}
            action="delete"
            entity="resposta"
            feminine
            name={responseToDelete?.respondent_name || responseToDelete?.respondent_email || undefined}
            warning="O participante poderá responder de novo pelo mesmo link."
          />
        </>
      )}
    </>
  )
}
